<template>
  <div class="tsp-check-row">
    <div class="tsp-check-name">
      <img class="tsp-check-icon" v-if="imgStatus" :src="imgStatus" alt="" />
      <el-tooltip
        popper-class="token-swap-popper token-swap-popper--enterable"
        placement="top-start"
        :enterable="true"
        manual
        v-model="showTip"
        v-if="i18nLabelTip"
      >
        <div slot="content" style="max-width: 200px" @mouseenter="onTipEnter" @mouseleave="onTipLeave">
          <template v-if="Array.isArray(i18nLabelTip)">
            <div v-for="(line, index) in i18nLabelTip" :key="index">
              {{ line }}
            </div>
          </template>
          <template v-else>
            <div>
              {{ checkRow.label === 'scheduled_fee_rate' ? i18nLabelTip : $t(i18nLabelTip) }}
            </div>
          </template>
          <template v-if="checkRow.addresses && checkRow.addresses.length">
            <div v-for="(address, index) in checkRow.addresses" :key="index">
              {{ address }}
            </div>
          </template>
        </div>
        <span @mouseenter="onRefEnter" @mouseleave="onRefLeave">
          {{ $t(i18nLabel) }}
        </span>
      </el-tooltip>
      <span v-else>{{ $t(i18nLabel) }}</span>
    </div>

    <div class="tsp-check-cols" :class="{ 'tsp-check-cols--single': isSingleColumn }">
      <div class="tsp-check-col-value" v-if="showGoPlus">
        {{ renderSourceResult(checkRow.goplus) }}
      </div>
      <div class="tsp-check-col-value" v-if="showCertik">
        {{ renderSourceResult(checkRow.certik) }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TokenSecurityCheckRow',
  props: {
    checkRow: {
      type: Object,
    },
    isSol: {
      type: Boolean,
    },
    epoch: {
      type: Number,
    },
    showGoPlus: {
      type: Boolean,
      default: true,
    },
    showCertik: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showTip: false,
      isRefHover: false,
      isTipHover: false,
      hideTipTimer: null,
    };
  },
  computed: {
    isSingleColumn() {
      return (this.showGoPlus && !this.showCertik) || (!this.showGoPlus && this.showCertik);
    },
    i18nLabelTip() {
      if (this.isSol) {
        if (this.checkRow && this.checkRow.label === 'transfer_hook') {
          if (this.checkRow.status === 1) {
            return 'solTokenSecurityData.' + this.checkRow.label + '_warning_tip';
          } else if (this.checkRow.status === 2) {
            return 'solTokenSecurityData.' + this.checkRow.label + '_danger_tip';
          }
          return '';
        }
        if (this.checkRow && this.checkRow.label === 'metadata_mutable') {
          if (this.checkRow.status === 1) {
            return 'solTokenSecurityData.' + this.checkRow.label + '_warning_tip';
          } else if (this.checkRow.status === 2) {
            return 'solTokenSecurityData.' + this.checkRow.label + '_danger_tip';
          }
          return '';
        }
        if (this.checkRow && this.checkRow.label === 'scheduled_fee_rate') {
          const scheduleList = Array.isArray(this.checkRow.scheduleList) ? this.checkRow.scheduleList : [];
          const currentEpoch = Number(this.epoch) || 0;

          if (!scheduleList.length) {
            return this.$t('solTokenSecurityData.scheduled_fee_rate_tip');
          }

          const formatRate = (rate) => {
            const num = Number(rate);
            return Number.isFinite(num) ? num.toFixed(2) : rate;
          };

          if (scheduleList.length === 1) {
            const only = scheduleList[0];
            const tipIndex = currentEpoch <= only.epoch ? 1 : 2;
            return this.$t(`solTokenSecurityData.scheduled_fee_rate_tip_${tipIndex}`, {
              epoch1: only.epoch,
              tax: formatRate(only.fee_rate),
              epoch2: currentEpoch,
            });
          }

          const sorted = [...scheduleList].sort((a, b) => a.epoch - b.epoch);
          const first = sorted[0];
          const last = sorted[sorted.length - 1];
          const lines = [];

          if (currentEpoch <= first.epoch) {
            sorted.forEach((item) => {
              lines.push(
                this.$t('solTokenSecurityData.scheduled_fee_rate_tip_future', {
                  epoch1: item.epoch,
                  tax: formatRate(item.fee_rate),
                }),
              );
            });
            lines.push(this.$t('solTokenSecurityData.scheduled_fee_rate_tip_current_epoch', { epoch2: currentEpoch }));
            return lines;
          }

          if (currentEpoch >= last.epoch) {
            const applied = [...sorted].reverse().find((item) => currentEpoch >= item.epoch) || last;
            lines.push(
              this.$t('solTokenSecurityData.scheduled_fee_rate_tip_past', {
                epoch1: applied.epoch,
                tax: formatRate(applied.fee_rate),
              }),
            );
            lines.push(this.$t('solTokenSecurityData.scheduled_fee_rate_tip_current_epoch', { epoch2: currentEpoch }));
            return lines;
          }

          const applied = [...sorted].reverse().find((item) => currentEpoch >= item.epoch) || first;
          const next = sorted.find((item) => currentEpoch < item.epoch) || last;
          lines.push(
            this.$t('solTokenSecurityData.scheduled_fee_rate_tip_past', {
              epoch1: applied.epoch,
              tax: formatRate(applied.fee_rate),
            }),
          );
          lines.push(
            this.$t('solTokenSecurityData.scheduled_fee_rate_tip_future', {
              epoch1: next.epoch,
              tax: formatRate(next.fee_rate),
            }),
          );
          lines.push(this.$t('solTokenSecurityData.scheduled_fee_rate_tip_current_epoch', { epoch2: currentEpoch }));
          return lines;
        }
        return 'solTokenSecurityData.' + this.checkRow.label + '_tip';
      }
      return 'tokenSecurityData.' + this.checkRow.label + '_tip';
    },
    i18nLabel() {
      if (this.isSol) {
        return 'solTokenSecurityData.' + this.checkRow.label;
      }
      return 'tokenSecurityData.' + this.checkRow.label;
    },
    tspWarningIcon() {
      return require(`@/assets/red.png`);
    },
    imgStatus() {
      if (this.checkRow) {
        if (this.checkRow.status === 0) {
          return require('@/assets/token-security/green.png');
        } else if (this.checkRow.status === 1) {
          return require('@/assets/token-security/warning.png');
        } else if (this.checkRow.status === 2) {
          return require('@/assets/token-security/error-red.png');
        } else {
          return require(`@/assets/token-security/status-3${this.darkModeImg}.png`);
        }
      } else {
        return '';
      }
    },
  },
  beforeDestroy() {
    if (this.hideTipTimer) {
      clearTimeout(this.hideTipTimer);
      this.hideTipTimer = null;
    }
  },
  methods: {
    renderSourceResult(sourceObj) {
      if (!sourceObj || sourceObj.result === 'unknown') {
        return '-';
      }
      return sourceObj.result;
    },
    syncTipVisibility() {
      this.showTip = this.isRefHover || this.isTipHover;
    },
    deferSyncTipVisibility() {
      if (this.hideTipTimer) {
        clearTimeout(this.hideTipTimer);
      }
      this.hideTipTimer = setTimeout(() => {
        this.syncTipVisibility();
      }, 100);
    },
    onRefEnter() {
      this.isRefHover = true;
      this.syncTipVisibility();
    },
    onRefLeave() {
      this.isRefHover = false;
      this.deferSyncTipVisibility();
    },
    onTipEnter() {
      this.isTipHover = true;
      this.syncTipVisibility();
    },
    onTipLeave() {
      this.isTipHover = false;
      this.deferSyncTipVisibility();
    },
  },
};
</script>

<style scoped lang="scss">
.tsp-check-row {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 10px;

  &:first-child {
    border-top: 0;
  }

  .tsp-check-name {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;

    .tsp-check-icon {
      width: 18px;
      height: 18px;
      border-radius: 6px;
      display: grid;
      place-items: center;
      font-weight: 900;
      color: #fff;
    }

    span {
      color: var(--color-29);
      text-decoration: underline dashed;
      text-underline-offset: 3px;
    }
  }

  .tsp-check-cols {
    flex-shrink: 0;
    width: 52px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    align-items: center;

    &--single {
      width: 20px;
      gap: 0;
      justify-content: flex-end;
    }

    .tsp-check-col-value {
      width: 20px;
      font-size: 12px;
      font-weight: 500;
      text-align: center;
      color: var(--color-29);
    }
  }
}
</style>
